/**
 * LocalIndex — SQLite-backed IAssetIndex implementation.
 *
 * Storage: ~/.skaile/index.db by default; override with `SKAILE_INDEX_PATH`.
 * Library checkouts live separately under `~/.skaile/libraries/<name>/` and
 * are controlled by `SKAILE_LIBRARIES_DIR`.
 *
 * All four entity tables (libraries, asset_definitions, instances, assignments)
 * are created on first open. See db.ts for DDL.
 *
 * @docLink packages/library/concepts#local-index
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";
import type { IAssetKindRegistry } from "@skaile/workspaces/plugins";
import type { InstallManifest } from "@skaile/workspaces/types";
import { and, eq, like } from "drizzle-orm";
import { resolveIndexPath, resolveLibraryDir } from "../config.js";
import { type AssetFetcher, installFromManifest } from "../install/install-from-manifest.js";
import type {
  AssetDefinition,
  AssetFilter,
  Assignment,
  CreateInstanceInput,
  IAssetIndex,
  Instance,
  InstanceFilter,
  PinPolicy,
  SyncResult,
  UpdateInstanceInput,
} from "../library.js";
import {
  AssignmentNotFoundError,
  DuplicateAssignmentError,
  InstanceHasConsumersError,
  InstanceNotFoundError,
  SourceNotFoundError,
} from "../library.js";
import type { AddLibraryInput, BackendConfig, Library, LibraryBackend } from "../user-library.js";
import { createLibraryDb, type LibraryDb, type LibraryDbHandle } from "./db.js";
import * as schema from "./schema.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Escape SQL LIKE wildcards so user-provided prefix is treated as literal. */
function escapeLikePrefix(prefix: string): string {
  return prefix.replace(/[%_]/g, "\\$&");
}

/**
 * Strip the trailing `@<version>` off a `<publisher>/<name>@<version>` ref,
 * leaving the version-agnostic base. A leading `@` is the scope sigil, not a
 * version separator, so we key off the *last* `@`.
 */
function stripRefVersion(ref: string): string {
  const at = ref.lastIndexOf("@");
  return at > 0 ? ref.slice(0, at) : ref;
}

/** Parse the JSON `soft_deps` column into a string[]; tolerate null/garbage. */
// null (legacy/unsynced rows) → undefined ("unknown"); "[]" → [] ("known empty").
function parseSoftDeps(raw: string | null): string[] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    return parsed.filter((s): s is string => typeof s === "string");
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Row mappers
// ---------------------------------------------------------------------------

function rowToLibrary(row: typeof schema.libraries.$inferSelect): Library {
  const cfg = (() => {
    try {
      return JSON.parse(row.backendConfig) as BackendConfig;
    } catch {
      return {} as BackendConfig;
    }
  })();
  return {
    id: row.id,
    name: row.name,
    path: row.path,
    backend: row.backend as LibraryBackend,
    backendConfig: cfg,
    ownership: row.ownership as Library["ownership"],
    structure: (row.structure as Library["structure"]) ?? undefined,
    isDefault: row.isDefault,
    manifestGenerated: row.manifestGenerated,
    lastSyncAt: row.lastSyncAt ? new Date(row.lastSyncAt) : undefined,
    createdAt: new Date(row.createdAt),
    updatedAt: new Date(row.updatedAt),
  };
}

function rowToAssetDef(row: typeof schema.assetDefinitions.$inferSelect): AssetDefinition {
  return {
    id: row.id,
    kind: row.kind,
    publisher: row.publisher,
    name: row.name,
    version: row.version,
    sha256: row.sha256 ?? undefined,
    description: row.description ?? undefined,
    license: row.license ?? undefined,
    category: row.category ?? undefined,
    manifest: JSON.parse(row.manifest),
    libraryId: row.libraryId ?? undefined,
    softDeps: parseSoftDeps(row.softDeps),
    kindProviderVersion: row.kindProviderVersion ?? undefined,
    cachedAt: new Date(row.cachedAt),
    updatedAt: new Date(row.updatedAt),
  };
}

function rowToInstance(row: typeof schema.instances.$inferSelect): Instance {
  return {
    id: row.id,
    defRef: row.defRef,
    defPin: row.defPin as PinPolicy,
    config: JSON.parse(row.config),
    credentialRef: row.credentialRef ?? undefined,
    createdAt: new Date(row.createdAt),
    updatedAt: new Date(row.updatedAt),
    createdBy: row.createdBy ?? undefined,
    sourceCommitSha: row.sourceCommitSha ?? undefined,
  };
}

function rowToAssignment(row: typeof schema.assignments.$inferSelect): Assignment {
  return {
    id: row.id,
    workspaceId: row.workspaceId,
    instanceId: row.instanceId,
    pinPolicy: row.pinPolicy as PinPolicy,
    assignedAt: new Date(row.assignedAt),
  };
}

// ---------------------------------------------------------------------------
// Constructor options
// ---------------------------------------------------------------------------

export interface LocalIndexOptions {
  /** Override the library checkouts directory (where `library init` places dirs). */
  libraryDir?: string;
  /** Override the absolute path to the index SQLite file (default: ~/.skaile/index.db). */
  indexPath?: string;
  /** Kind registry for provider-version tracking and kind validation. */
  kindRegistry?: IAssetKindRegistry;
}

// ---------------------------------------------------------------------------
// LocalIndex
// ---------------------------------------------------------------------------

/**
 * SQLite-backed implementation of {@link IAssetIndex}.
 *
 * Backed by `@libsql/client`, so it runs on both Node and Bun. Stores data at
 * `~/.skaile/index.db` by default; override with `SKAILE_INDEX_PATH` (or via
 * the constructor `indexPath` option). Library checkouts live separately at
 * `resolveLibraryDir()` (default `~/.skaile/libraries/`).
 *
 * The constructor is synchronous — it only resolves the storage path. The
 * SQLite connection is opened lazily on first use (`@libsql/client` is
 * async-only). Always call {@link LocalIndex.close} in a `finally` block to
 * release the connection.
 *
 * @docLink packages/library/concepts#local-index
 */
export class LocalIndex implements IAssetIndex {
  private readonly dbPath: string;
  private dbHandleRef: LibraryDbHandle | null = null;
  private dbPromise: Promise<LibraryDbHandle> | null = null;
  private _kindRegistry: IAssetKindRegistry | undefined;
  /** Absolute path to the library checkouts directory. */
  private readonly libraryDir: string;

  /**
   * Construct a LocalIndex. Synchronous — does not open the DB.
   *
   * @param optionsOrDir - Either a `LocalIndexOptions` object or a string
   *   path (legacy signature: treated as the libraryDir, with the index file
   *   landing at `<dir>/index.db`).
   */
  constructor(optionsOrDir?: string | LocalIndexOptions) {
    let dir: string;
    let dbPath: string;
    if (typeof optionsOrDir === "string") {
      dir = optionsOrDir;
      dbPath = path.join(dir, "index.db");
    } else if (optionsOrDir?.indexPath) {
      dbPath = optionsOrDir.indexPath;
      dir = optionsOrDir.libraryDir ?? resolveLibraryDir();
      this._kindRegistry = optionsOrDir.kindRegistry;
    } else if (optionsOrDir?.libraryDir) {
      dir = optionsOrDir.libraryDir;
      dbPath = path.join(dir, "index.db");
      this._kindRegistry = optionsOrDir.kindRegistry;
    } else {
      dir = resolveLibraryDir();
      dbPath = resolveIndexPath();
      this._kindRegistry = optionsOrDir?.kindRegistry;
    }
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }
    const dbParent = path.dirname(dbPath);
    if (!fs.existsSync(dbParent)) {
      fs.mkdirSync(dbParent, { recursive: true });
    }
    this.libraryDir = dir;
    this.dbPath = dbPath;
  }

  /**
   * Lazily open the SQLite DB on first use. The `??=` dedupes concurrent
   * first calls so the DB is opened exactly once, with no race.
   */
  private async ensureDb(): Promise<LibraryDb> {
    if (this.dbHandleRef) return this.dbHandleRef.db;
    this.dbPromise ??= createLibraryDb(this.dbPath);
    this.dbHandleRef = await this.dbPromise;
    return this.dbHandleRef.db;
  }

  /** The kind registry bound to this library (if any). */
  get kinds(): IAssetKindRegistry | undefined {
    return this._kindRegistry;
  }

  /** Close the underlying SQLite connection. Call in tests / cleanup. */
  close(): void {
    if (this.dbHandleRef) this.dbHandleRef.close();
    this.dbHandleRef = null;
    this.dbPromise = null;
  }

  /** Internal access for LibraryManager. Do not call from user code. */
  async dbHandle(): Promise<LibraryDb> {
    return this.ensureDb();
  }

  // -- Libraries (formerly "Sources") --------------------------------------

  async listSources(): Promise<Library[]> {
    const db = await this.ensureDb();
    const rows = await db.select().from(schema.libraries).all();
    return rows.map(rowToLibrary);
  }

  async addSource(input: AddLibraryInput): Promise<Library> {
    const db = await this.ensureDb();
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await db
      .insert(schema.libraries)
      .values({
        id,
        name: input.name,
        path: input.path,
        backend: input.backend,
        backendConfig: JSON.stringify(input.backendConfig ?? {}),
        ownership: input.ownership ?? (input.backend === "local" ? "owner" : "reader"),
        isDefault: input.isDefault ?? false,
        manifestGenerated: input.manifestGenerated ?? false,
        structure: input.structure ?? null,
        createdAt: now,
        updatedAt: now,
      })
      .run();
    const row = (await db
      .select()
      .from(schema.libraries)
      .where(eq(schema.libraries.id, id))
      .get())!;
    return rowToLibrary(row);
  }

  async removeSource(id: string): Promise<void> {
    const db = await this.ensureDb();
    const existing = await db
      .select()
      .from(schema.libraries)
      .where(eq(schema.libraries.id, id))
      .get();
    if (!existing) throw new SourceNotFoundError(id);
    await db.delete(schema.libraries).where(eq(schema.libraries.id, id)).run();
  }

  async syncSource(id: string): Promise<SyncResult> {
    const db = await this.ensureDb();
    const existing = await db
      .select()
      .from(schema.libraries)
      .where(eq(schema.libraries.id, id))
      .get();
    if (!existing) throw new SourceNotFoundError(id);

    // Phase 1 stub: discovery infrastructure lands in Task 1.5.
    // Update last_sync_at + updated_at and return empty result.
    const now = new Date().toISOString();
    await db
      .update(schema.libraries)
      .set({ lastSyncAt: now, updatedAt: now })
      .where(eq(schema.libraries.id, id))
      .run();

    return { libraryId: id, assetsFound: 0, assetsUpdated: 0, errors: [] };
  }

  // -- Asset definitions ---------------------------------------------------

  /**
   * Insert or update a cached asset definition.
   *
   * Not on the IAssetIndex interface -- used by catalog sources (Task 1.5)
   * and tests. INSERT OR REPLACE semantics.
   */
  async upsertAssetDef(def: AssetDefinition): Promise<void> {
    const db = await this.ensureDb();
    const now = new Date().toISOString();
    // Resolve kindProviderVersion from registry if not explicitly set
    let providerVersion = def.kindProviderVersion ?? null;
    if (!providerVersion && this._kindRegistry) {
      const provider = this._kindRegistry.getProvider(def.kind);
      if (provider) {
        providerVersion = provider.providerVersion;
      }
    }

    await db
      .insert(schema.assetDefinitions)
      .values({
        id: def.id,
        kind: def.kind,
        publisher: def.publisher,
        name: def.name,
        version: def.version,
        sha256: def.sha256 ?? null,
        description: def.description ?? null,
        license: def.license ?? null,
        category: def.category ?? null,
        manifest: JSON.stringify(def.manifest),
        libraryId: def.libraryId ?? null,
        softDeps: def.softDeps ? JSON.stringify(def.softDeps) : null,
        kindProviderVersion: providerVersion,
        cachedAt: def.cachedAt?.toISOString() ?? now,
        updatedAt: now,
      })
      .onConflictDoUpdate({
        target: schema.assetDefinitions.id,
        set: {
          kind: def.kind,
          publisher: def.publisher,
          name: def.name,
          version: def.version,
          sha256: def.sha256 ?? null,
          description: def.description ?? null,
          license: def.license ?? null,
          category: def.category ?? null,
          manifest: JSON.stringify(def.manifest),
          libraryId: def.libraryId ?? null,
          softDeps: def.softDeps ? JSON.stringify(def.softDeps) : null,
          kindProviderVersion: providerVersion,
          updatedAt: now,
        },
      })
      .run();
  }

  /**
   * Delete this library's cached asset defs whose ref is not in `keepRefs`.
   * Makes a sync authoritative: a def re-keyed under a new publisher (e.g.
   * `@skills/x` → `@acme/x`) leaves no stale duplicate behind. Returns the
   * number of rows removed.
   *
   * Not on the IAssetIndex interface — used by catalog sources.
   */
  async pruneAssetDefs(libraryId: string, keepRefs: string[]): Promise<number> {
    const db = await this.ensureDb();
    const rows = await db
      .select({ id: schema.assetDefinitions.id })
      .from(schema.assetDefinitions)
      .where(eq(schema.assetDefinitions.libraryId, libraryId))
      .all();
    const keep = new Set(keepRefs);
    const stale = rows.filter((r) => !keep.has(r.id)).map((r) => r.id);
    for (const id of stale) {
      await db
        .delete(schema.assetDefinitions)
        .where(
          and(eq(schema.assetDefinitions.libraryId, libraryId), eq(schema.assetDefinitions.id, id)),
        )
        .run();
    }
    return stale.length;
  }

  async getAssetDef(ref: string): Promise<AssetDefinition | null> {
    const db = await this.ensureDb();
    const row = await db
      .select()
      .from(schema.assetDefinitions)
      .where(eq(schema.assetDefinitions.id, ref))
      .get();
    return row ? rowToAssetDef(row) : null;
  }

  async listAssetDefs(filter?: AssetFilter): Promise<AssetDefinition[]> {
    const db = await this.ensureDb();
    if (!filter) {
      return (await db.select().from(schema.assetDefinitions).all()).map(rowToAssetDef);
    }

    const conditions = [];
    if (filter.kind) conditions.push(eq(schema.assetDefinitions.kind, filter.kind));
    if (filter.publisher) conditions.push(eq(schema.assetDefinitions.publisher, filter.publisher));
    if (filter.prefix)
      conditions.push(like(schema.assetDefinitions.id, `${escapeLikePrefix(filter.prefix)}%`));
    if (filter.libraryId) conditions.push(eq(schema.assetDefinitions.libraryId, filter.libraryId));

    const where = conditions.length > 0 ? and(...conditions) : undefined;
    let rows = where
      ? await db.select().from(schema.assetDefinitions).where(where).all()
      : await db.select().from(schema.assetDefinitions).all();

    if (filter.offset) rows = rows.slice(filter.offset);
    if (filter.limit) rows = rows.slice(0, filter.limit);

    return rows.map(rowToAssetDef);
  }

  // -- Instances -----------------------------------------------------------

  async createInstance(input: CreateInstanceInput): Promise<Instance> {
    const db = await this.ensureDb();
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const pin = input.defPin ?? "minor-track";
    const config = input.config ?? {};

    await db
      .insert(schema.instances)
      .values({
        id,
        defRef: input.defRef,
        defPin: pin,
        config: JSON.stringify(config),
        credentialRef: input.credentialRef ?? null,
        createdAt: now,
        updatedAt: now,
        createdBy: input.createdBy ?? null,
        sourceCommitSha: null,
      })
      .run();

    return {
      id,
      defRef: input.defRef,
      defPin: pin,
      config,
      credentialRef: input.credentialRef,
      createdAt: new Date(now),
      updatedAt: new Date(now),
      createdBy: input.createdBy,
    };
  }

  /**
   * Install an asset from a pointer-only {@link InstallManifest}.
   *
   * This is the pointer-only install path: the caller obtains the manifest
   * from a catalog source (`RemoteCatalogSource.getInstallManifest(ref)` —
   * a `RemoteCatalogSource`-only method, not on the `ICatalogSource`
   * contract), and `install` then:
   *
   *   1. fetches the asset bytes directly from the upstream repo at the
   *      pinned commit and verifies every per-file + the composite SHA256
   *      (via {@link installFromManifest});
   *   2. records an {@link Instance} row carrying `sourceCommitSha` so the
   *      install is auditable and re-verifiable.
   *
   * Not part of the {@link IAssetIndex} interface — `LocalIndex`-only, since
   * the platform variant resolves install bytes differently.
   *
   * @param manifest - The pointer-only install manifest from the Catalog.
   * @param opts.libraryRoot - Filesystem root to install asset bytes under.
   *   Defaults to an `installed/` subdirectory of the library storage dir.
   * @param opts.fetcher - Pluggable byte fetcher (tests inject a mock);
   *   defaults to `fetchAssetFilesFromGitHub`.
   * @param opts.config - Initial Instance config. Defaults to `{}`.
   * @param opts.defPin - Pin policy for the Instance. Defaults to `exact`
   *   (a pointer-only install is pinned to an immutable commit).
   * @returns The created Instance plus its on-disk install path.
   */
  async install(
    manifest: InstallManifest,
    opts?: {
      libraryRoot?: string;
      fetcher?: AssetFetcher;
      config?: Record<string, unknown>;
      defPin?: PinPolicy;
      createdBy?: string;
    },
  ): Promise<{ instance: Instance; installPath: string }> {
    const libraryRoot = opts?.libraryRoot ?? path.join(this.libraryDir, "installed");

    const { installPath } = await installFromManifest({
      manifest,
      libraryRoot,
      fetcher: opts?.fetcher,
    });

    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const pin: PinPolicy = opts?.defPin ?? "exact";
    const config = opts?.config ?? {};

    const db = await this.ensureDb();
    await db
      .insert(schema.instances)
      .values({
        id,
        defRef: manifest.ref,
        defPin: pin,
        config: JSON.stringify(config),
        credentialRef: null,
        createdAt: now,
        updatedAt: now,
        createdBy: opts?.createdBy ?? null,
        sourceCommitSha: manifest.source.commitSha,
      })
      .run();

    return {
      instance: {
        id,
        defRef: manifest.ref,
        defPin: pin,
        config,
        createdAt: new Date(now),
        updatedAt: new Date(now),
        createdBy: opts?.createdBy,
        sourceCommitSha: manifest.source.commitSha,
      },
      installPath,
    };
  }

  async getInstance(id: string): Promise<Instance | null> {
    const db = await this.ensureDb();
    const row = await db.select().from(schema.instances).where(eq(schema.instances.id, id)).get();
    return row ? rowToInstance(row) : null;
  }

  async listInstances(filter?: InstanceFilter): Promise<Instance[]> {
    const db = await this.ensureDb();
    if (!filter) {
      return (await db.select().from(schema.instances).all()).map(rowToInstance);
    }

    const conditions = [];
    if (filter.defRef) conditions.push(eq(schema.instances.defRef, filter.defRef));
    if (filter.defPin) conditions.push(eq(schema.instances.defPin, filter.defPin));

    const where = conditions.length > 0 ? and(...conditions) : undefined;
    let rows = where
      ? await db.select().from(schema.instances).where(where).all()
      : await db.select().from(schema.instances).all();

    // Kind lives on AssetDefinition, not on the instances table, so filter it
    // post-query by mapping each instance's version-stripped defRef base back
    // to its definition's kind. Pagination runs after so counts are post-filter.
    if (filter.kind) {
      const kindByBase = await this.buildInstanceKindMap(db);
      rows = rows.filter((r) => kindByBase.get(stripRefVersion(r.defRef)) === filter.kind);
    }

    if (filter.offset) rows = rows.slice(filter.offset);
    if (filter.limit) rows = rows.slice(0, filter.limit);

    return rows.map(rowToInstance);
  }

  /** Map every cached asset def's version-stripped ref base to its kind. */
  private async buildInstanceKindMap(db: LibraryDb): Promise<Map<string, string>> {
    const defs = await db.select().from(schema.assetDefinitions).all();
    const map = new Map<string, string>();
    for (const d of defs) map.set(stripRefVersion(d.id), d.kind);
    return map;
  }

  async updateInstance(id: string, patch: UpdateInstanceInput): Promise<Instance> {
    const db = await this.ensureDb();
    const existing = await db
      .select()
      .from(schema.instances)
      .where(eq(schema.instances.id, id))
      .get();
    if (!existing) throw new InstanceNotFoundError(id);

    const now = new Date().toISOString();
    const updates: Record<string, unknown> = { updatedAt: now };
    if (patch.config !== undefined) updates.config = JSON.stringify(patch.config);
    if (patch.credentialRef !== undefined) updates.credentialRef = patch.credentialRef;
    if (patch.defPin !== undefined) updates.defPin = patch.defPin;

    await db.update(schema.instances).set(updates).where(eq(schema.instances.id, id)).run();

    const updated = (await db
      .select()
      .from(schema.instances)
      .where(eq(schema.instances.id, id))
      .get())!;
    return rowToInstance(updated);
  }

  async deleteInstance(id: string, opts?: { cascade?: boolean }): Promise<void> {
    const db = await this.ensureDb();
    const existing = await db
      .select()
      .from(schema.instances)
      .where(eq(schema.instances.id, id))
      .get();
    if (!existing) throw new InstanceNotFoundError(id);

    // Check for active consumers
    const consumers = await db
      .select()
      .from(schema.assignments)
      .where(eq(schema.assignments.instanceId, id))
      .all();

    if (consumers.length > 0 && !opts?.cascade) {
      throw new InstanceHasConsumersError(id, consumers.length);
    }

    // FK CASCADE handles assignment cleanup automatically.
    await db.delete(schema.instances).where(eq(schema.instances.id, id)).run();
  }

  // -- Assignments ---------------------------------------------------------

  async assign(workspaceId: string, instanceId: string, pin: PinPolicy): Promise<Assignment> {
    const db = await this.ensureDb();
    // Verify instance exists
    const instance = await db
      .select()
      .from(schema.instances)
      .where(eq(schema.instances.id, instanceId))
      .get();
    if (!instance) throw new InstanceNotFoundError(instanceId);

    // Check for duplicate
    const existing = await db
      .select()
      .from(schema.assignments)
      .where(
        and(
          eq(schema.assignments.workspaceId, workspaceId),
          eq(schema.assignments.instanceId, instanceId),
        ),
      )
      .get();
    if (existing) throw new DuplicateAssignmentError(workspaceId, instanceId);

    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await db
      .insert(schema.assignments)
      .values({ id, workspaceId, instanceId, pinPolicy: pin, assignedAt: now })
      .run();

    return { id, workspaceId, instanceId, pinPolicy: pin, assignedAt: new Date(now) };
  }

  async unassign(assignmentId: string): Promise<void> {
    const db = await this.ensureDb();
    const existing = await db
      .select()
      .from(schema.assignments)
      .where(eq(schema.assignments.id, assignmentId))
      .get();
    if (!existing) throw new AssignmentNotFoundError(assignmentId);

    await db.delete(schema.assignments).where(eq(schema.assignments.id, assignmentId)).run();
  }

  async listAssignments(workspaceId: string): Promise<Assignment[]> {
    const db = await this.ensureDb();
    return (
      await db
        .select()
        .from(schema.assignments)
        .where(eq(schema.assignments.workspaceId, workspaceId))
        .all()
    ).map(rowToAssignment);
  }

  async getConsumptionGraph(instanceId: string): Promise<Assignment[]> {
    const db = await this.ensureDb();
    return (
      await db
        .select()
        .from(schema.assignments)
        .where(eq(schema.assignments.instanceId, instanceId))
        .all()
    ).map(rowToAssignment);
  }
}
